/**
 * Bay Image Card Component
 *
 * Displays a bay image with the bay name and description overlaid at the bottom.
 * Falls back to a placeholder image if no image is provided.
 *
 * ## Features
 * - Mobile-first responsive design
 * - Automatic fallback to placeholder images
 * - Semantic HTML with proper alt text
 * - WCAG 2.1 AA compliant
 * - Text overlaid on image with gradient for readability
 * - Fills full height of container
 *
 * @module butterUI/components/BayImageCard
 */

import { getBayImageUrl } from '#utils';
import type { BayImageCardProps } from "./types";

/**
 * Bay Image Card
 *
 * @example
 * ```tsx
 * <BayImageCard
 *   bay={{
 *     id: 1,
 *     name: 'Bay 1',
 *     description: 'VIP golf bay with professional equipment',
 *     image: { url: '/images/bay1.jpg', alt: 'Bay 1 interior' }
 *   }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // Without image - uses placeholder
 * <BayImageCard
 *   bay={{ id: 1, name: 'Bay 1', description: 'Standard bay' }}
 *   index={0}
 * />
 * ```
 */
export function BayImageCard({
  bay,
  description,
  index = 0,
  className = "",
}: BayImageCardProps) {
  const imageUrl = getBayImageUrl(bay, index);
  const bayName = bay?.name || "Golf Bay";
  const bayDescription = description || bay?.description;
  const altText = bay?.image?.alt || `${bayName} interior view`;

  return (
    <div
      className={`relative aspect-[4/3] overflow-hidden rounded-28 bg-gray-900 shadow-sm lg:aspect-auto lg:h-full lg:min-h-[450px] ${className}`}
    >
      {/* Bay Image - fills entire container */}
      <img
        src={imageUrl}
        alt={altText}
        className="h-full w-full object-cover"
      />

      {/* Gradient overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

      {/* Bay Info Overlaid at Bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-7">
        <h3 className="text-2xl font-bold text-white mb-1">{bayName}</h3>
        {bayDescription && (
          <p className="text-sm text-white/80">{bayDescription}</p>
        )}
      </div>
    </div>
  );
}
